/* eslint-disable prettier/prettier */
import React, { useEffect, useRef } from 'react';
import {
	CalendarViewMode,
	MonthRange,
	ScheduleViewMode,
	WeekRange,
} from '../../types/schedule.types';
import CalendarHeader from './CalendarHeader';
import CalendarGrid from './CalendarGrid';
import MonthPicker from './MonthPicker';
import YearPicker from './YearPicker';
import styles from '../../styles/scheduler/CalendarPopup.module.css';

const today = new Date();

interface CalendarPopupProps {
	isOpen: boolean;
	/** 'week' -> rows select a week. 'month' -> any click selects the month. */
	viewMode: ScheduleViewMode;
	month: number;
	year: number;
	calendarView: CalendarViewMode;
	selectedWeek: WeekRange | null;
	selectedMonth: MonthRange | null;
	eventDots: Set<string>;
	onClose: () => void;
	onSelectWeek: (week: WeekRange) => void;
	onSelectMonthRange: (month: MonthRange) => void;
	onNavigateMonth: (dir: 'prev' | 'next') => void;
	onMonthClick: () => void;
	onYearClick: () => void;
	onSelectMonth: (month: number) => void;
	onSelectYear: (year: number) => void;
}

const CalendarPopup: React.FC<CalendarPopupProps> = ({
	isOpen,
	viewMode,
	month,
	year,
	calendarView,
	selectedWeek,
	selectedMonth,
	eventDots,
	onClose,
	onSelectWeek,
	onSelectMonthRange,
	onNavigateMonth,
	onMonthClick,
	onYearClick,
	onSelectMonth,
	onSelectYear,
}) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isOpen) return;
		const handler = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node))
				onClose();
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return (
		<div className={styles.popup} ref={ref} role="dialog" aria-modal="true">
			<CalendarHeader
				month={month}
				year={year}
				calendarView={calendarView}
				onPrev={() => onNavigateMonth('prev')}
				onNext={() => onNavigateMonth('next')}
				onMonthClick={onMonthClick}
				onYearClick={onYearClick}
			/>
			{calendarView === 'week' && (
				<CalendarGrid
					month={month}
					year={year}
					selectionMode={viewMode}
					selectedWeek={selectedWeek}
					selectedMonth={selectedMonth}
					eventDots={eventDots}
					onSelectWeek={onSelectWeek}
					onSelectMonthRange={onSelectMonthRange}
				/>
			)}
			{calendarView === 'month' && (
				<MonthPicker
					selectedMonth={
						viewMode === 'month' && selectedMonth
							? selectedMonth.year === year
								? selectedMonth.month
								: -1
							: month
					}
					currentMonth={
						today.getFullYear() === year ? today.getMonth() : -1
					}
					onSelectMonth={onSelectMonth}
				/>
			)}
			{calendarView === 'year' && (
				<YearPicker
					selectedYear={year}
					currentYear={today.getFullYear()}
					onSelectYear={onSelectYear}
				/>
			)}
		</div>
	);
};

export default CalendarPopup;
